/**
 * ============================================================================
 * GUIDED CREATION PAGE — SERVER-SIDE VERIFICATION
 * ============================================================================
 * 
 * FLOW:
 * 1. User pays on start.bloomwrightstudio.com (PayPal)
 * 2. Redirected to bloomwrightstudio.com/guided-creation?email=...&order_id=...
 * 3. This page verifies access SERVER-SIDE via /api/verify-payment
 * 4. Access granted based on DATABASE payment status ONLY
 * 
 * IMPORTANT:
 * - Do NOT rely on localStorage or session (cross-subdomain)
 * - All access verification must be server-side
 * - Check database for payment status using email + order_id
 * ============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { 
  Sparkles, 
  Lock, 
  Check, 
  ArrowRight, 
  Palette, 
  FileText, 
  Package,
  Lightbulb,
  Target,
  AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VERIFY_API = 'https://start.bloomwrightstudio.com/api';  // Verify against checkout subdomain

const GuidedCreationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  
  // State for server-side verification
  const [verificationStatus, setVerificationStatus] = useState('loading'); // loading, verified, not_verified, error
  const [purchaseInfo, setPurchaseInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Get parameters from URL (from redirect after PayPal payment)
  const emailParam = searchParams.get('email');
  const orderIdParam = searchParams.get('order_id');

  useEffect(() => {
    verifyAccessServerSide();
  }, [emailParam, orderIdParam, user]);

  const verifyAccessServerSide = async () => {
    setVerificationStatus('loading');
    
    // Determine which email to use for verification
    // Priority: URL param (from redirect) > signed-in user email
    const emailToVerify = emailParam || user?.email;
    
    if (!emailToVerify) {
      setVerificationStatus('not_verified');
      setErrorMessage('No email provided for verification');
      return;
    }

    try {
      // Build verification URL with all available params
      // Using VERIFY_API which points to start.bloomwrightstudio.com
      let verifyUrl = `${VERIFY_API}/verify-payment?email=${encodeURIComponent(emailToVerify)}`;
      if (orderIdParam) {
        verifyUrl += `&order_id=${encodeURIComponent(orderIdParam)}`;
      }

      const response = await fetch(verifyUrl);
      const data = await response.json();
      
      if (data.has_purchase) {
        setVerificationStatus('verified');
        setPurchaseInfo(data);
      } else {
        setVerificationStatus('not_verified');
        setErrorMessage('No purchase found for this email');
      }
    } catch (error) {
      console.error("Server-side verification error:", error);
      setVerificationStatus('error');
      setErrorMessage('Unable to verify access. Please try again.');
    }
  };

  const handleLogin = () => {
    // Preserve URL params after login
    const returnUrl = window.location.href;
    sessionStorage.setItem('checkoutReturnUrl', returnUrl);
    const redirectUrl = window.location.origin + '/guided-creation';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Loading state
  if (verificationStatus === 'loading') {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-[#78716C]">Verifying your access...</p>
          <p className="text-sm text-[#78716C]/70 mt-2">Checking payment status in database</p>
        </div>
      </div>
    );
  }

  // Error state
  if (verificationStatus === 'error') {
    return (
      <div className="min-h-screen bg-[#FDFCF8] px-6 py-20">
        <div className="max-w-2xl mx-auto text-center">
          <AlertCircle className="w-16 h-16 text-[#C77D63] mx-auto mb-6" />
          <h1 className="text-3xl text-[#2C332E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Verification Error
          </h1>
          <p className="text-lg text-[#78716C] mb-8">{errorMessage}</p>
          <Button 
            onClick={verifyAccessServerSide}
            className="bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full px-8 py-6"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Not verified - show paywall
  if (verificationStatus === 'not_verified') {
    return (
      <div className="min-h-screen bg-[#FDFCF8] px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Lock className="w-12 h-12 text-[#C77D63] mx-auto mb-4" />
            <h1 className="text-3xl md:text-4xl text-[#2C332E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Access Required
            </h1>
            <p className="text-lg text-[#78716C] max-w-2xl mx-auto">
              {emailParam 
                ? `No active purchase found for ${emailParam}. Please complete your purchase to access the Guided Creation tools.`
                : 'Sign in or complete a purchase to access the Guided Creation tools.'
              }
            </p>
          </div>

          {/* Value Proposition */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="bg-white rounded-2xl border-2 border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[#8DA399]/10 flex items-center justify-center mb-4">
                  <Lightbulb className="w-6 h-6 text-[#8DA399]" />
                </div>
                <CardTitle className="text-xl text-[#2C332E]">Brand Foundation</CardTitle>
                <CardDescription className="text-[#78716C]">
                  Get clarity on your brand's purpose, voice, and visual direction before you launch.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="bg-white rounded-2xl border-2 border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[#8DA399]/10 flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-[#8DA399]" />
                </div>
                <CardTitle className="text-xl text-[#2C332E]">Ideal Customer Profile</CardTitle>
                <CardDescription className="text-[#78716C]">
                  Understand who you're building for and what they truly value.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="bg-white rounded-2xl border-2 border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[#8DA399]/10 flex items-center justify-center mb-4">
                  <Palette className="w-6 h-6 text-[#8DA399]" />
                </div>
                <CardTitle className="text-xl text-[#2C332E]">Visual Identity Direction</CardTitle>
                <CardDescription className="text-[#78716C]">
                  Color moods, typography guidance, and aesthetic direction — all customizable.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="bg-white rounded-2xl border-2 border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[#8DA399]/10 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-[#8DA399]" />
                </div>
                <CardTitle className="text-xl text-[#2C332E]">Messaging Pillars</CardTitle>
                <CardDescription className="text-[#78716C]">
                  Core messages and content angles that work across any platform.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* CTA Section */}
          <div className="bg-[#F5F0EB] rounded-2xl p-8 text-center">
            <h2 className="text-2xl text-[#2C332E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Get Started
            </h2>
            <p className="text-[#78716C] mb-6 max-w-lg mx-auto">
              Complete your purchase to unlock the full Guided Creation experience.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              {!user && (
                <Button 
                  onClick={handleLogin}
                  className="bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full px-8 py-6 text-lg"
                >
                  Sign In
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}
              <Button 
                onClick={() => window.location.href = 'https://start.bloomwrightstudio.com'}
                className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-8 py-6 text-lg"
              >
                Purchase Access
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            <p className="text-sm text-[#78716C]">
              Already purchased? Make sure you're using the same email address.
            </p>
            
            {emailParam && (
              <button 
                onClick={verifyAccessServerSide}
                className="text-sm text-[#8DA399] hover:underline mt-4"
              >
                Re-check verification status
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // VERIFIED — FULL GUIDED CREATION DASHBOARD
  // Access granted based on database payment status
  // ============================================================================
  return (
    <div className="min-h-screen bg-[#FDFCF8] px-6 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Welcome Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#8DA399]/10 text-[#8DA399] px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Check className="w-4 h-4" />
            Access Verified — {purchaseInfo?.tier} Plan
          </div>
          <h1 className="text-3xl md:text-4xl text-[#2C332E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your Guided Creation Dashboard
          </h1>
          <p className="text-lg text-[#78716C] max-w-2xl mx-auto">
            Welcome! Build your brand foundation with clarity and intention.
          </p>
          {purchaseInfo?.email && (
            <p className="text-sm text-[#78716C]/70 mt-2">
              Verified for: {purchaseInfo.email}
            </p>
          )}
        </div>

        {/* Dashboard Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {/* Brand Foundation */}
          <Card 
            className="bg-white rounded-2xl border-2 border-[#8DA399]/30 shadow-soft hover:shadow-hover transition-all cursor-pointer" 
            onClick={() => navigate("/create")}
          >
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-xl bg-[#8DA399]/10 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-[#8DA399]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2C332E] mb-2">Create New Brand</h3>
              <p className="text-sm text-[#78716C] mb-4">
                Start the guided questionnaire to generate a new brand foundation.
              </p>
              <span className="text-[#8DA399] text-sm font-medium flex items-center gap-1">
                Begin <ArrowRight className="w-4 h-4" />
              </span>
            </CardContent>
          </Card>

          {/* My Brands */}
          <Card 
            className="bg-white rounded-2xl border-2 border-[#E6E1DB]/50 shadow-soft hover:shadow-hover transition-all cursor-pointer" 
            onClick={() => navigate("/dashboard")}
          >
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-xl bg-[#C77D63]/10 flex items-center justify-center mb-4">
                <Package className="w-6 h-6 text-[#C77D63]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2C332E] mb-2">My Brands</h3>
              <p className="text-sm text-[#78716C] mb-4">
                View and manage your saved brand foundations.
              </p>
              <span className="text-[#C77D63] text-sm font-medium flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </span>
            </CardContent>
          </Card>

          {/* Brand Guide PDF */}
          <Card 
            className="bg-white rounded-2xl border-2 border-[#E6E1DB]/50 shadow-soft hover:shadow-hover transition-all cursor-pointer" 
            onClick={() => navigate("/dashboard")}
          >
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-xl bg-[#8DA399]/10 flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-[#8DA399]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2C332E] mb-2">Download Guides</h3>
              <p className="text-sm text-[#78716C] mb-4">
                Access your brand guide PDFs for any activated brand.
              </p>
              <span className="text-[#8DA399] text-sm font-medium flex items-center gap-1">
                Access <ArrowRight className="w-4 h-4" />
              </span>
            </CardContent>
          </Card>
        </div>

        {/* What's Included Section */}
        <div className="bg-[#F5F0EB] rounded-2xl p-8 mb-12">
          <h2 className="text-2xl text-[#2C332E] mb-6 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
            What's Included in Your Plan
          </h2>
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              "Brand Foundation Overview",
              "Brand Identity Framework",
              "Ideal Customer Profile",
              "Messaging Pillars (3-4)",
              "Visual Identity Direction",
              "Launch Clarity Checklist",
              "Downloadable PDF Guide",
              "Unlimited Brand Iterations"
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#8DA399] flex-shrink-0" />
                <span className="text-[#2C332E]">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Support Section */}
        <div className="text-center">
          <p className="text-[#78716C] mb-4">
            Need help with your brand? Have questions?
          </p>
          <Button 
            variant="outline"
            className="border-[#E6E1DB] text-[#78716C] hover:bg-[#F5F0EB] rounded-full"
            onClick={() => window.open('mailto:support@bloomwrightstudio.com')}
          >
            Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GuidedCreationPage;
